import type { Stripe } from 'stripe';

/**
 * Reading a discount back out of what dabble-rest hands us. Stripe exposes the
 * same coupon under several shapes — a legacy singular `discount`, a stackable
 * `discounts[]`, line-item discounts, and either the 2024+ `source.coupon` or the
 * legacy top-level `coupon` on each row — and some rows come back as bare `di_…`
 * ids when nothing expanded them. These helpers normalise all of that so
 * BillingDiscountAlert and BillingCouponLinkModal (DAB-1431) read a subscription's
 * discount the same way.
 */

export type DiscountRow = Record<string, unknown>;

/** Synthetic row when Stripe returns only an unexpanded discount id (`di_…`) on the subscription. */
export const UNEXPANDED_DISCOUNT = '__dabble_unexpanded_discount';

export function collectDiscountEntries(source: DiscountRow): unknown[] {
  const out: unknown[] = [];
  if (source.discount != null) out.push(source.discount);
  const top = source.discounts;
  if (Array.isArray(top)) out.push(...top);
  // Subscriptions expose line items at .items.data[]; invoices at .lines.data[].
  for (const key of ['items', 'lines'] as const) {
    const items = (source[key] as { data?: unknown[] } | undefined)?.data;
    if (!Array.isArray(items)) continue;
    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      const idisc = (item as DiscountRow).discounts;
      if (Array.isArray(idisc)) out.push(...idisc);
    }
  }
  return out;
}

/** Resolve expanded Stripe.Coupon from a discount row (API 2024+ uses `source.coupon`; legacy uses top-level `coupon`). */
export function resolveCouponFromDiscountRow(raw: DiscountRow): Stripe.Coupon | null {
  const legacy = raw.coupon;
  if (legacy && typeof legacy === 'object') return legacy as Stripe.Coupon;
  const source = raw.source;
  if (source && typeof source === 'object') {
    const c = (source as DiscountRow).coupon;
    if (c && typeof c === 'object') return c as Stripe.Coupon;
  }
  return null;
}

/** The expanded coupons behind a subscription's or invoice's discounts. */
export function discountCoupons(source: DiscountRow): Stripe.Coupon[] {
  return collectDiscountEntries(source).flatMap((entry) => {
    const coupon = entry && typeof entry === 'object' ? resolveCouponFromDiscountRow(entry as DiscountRow) : null;
    return coupon ? [coupon] : [];
  });
}

export function discountRowLooksExpanded(r: DiscountRow): boolean {
  if (r.coupon != null || r.promotion_code != null) return true;
  const src = r.source;
  if (src && typeof src === 'object') {
    const s = src as DiscountRow;
    if (s.coupon != null || s.type === 'coupon') return true;
  }
  return false;
}

/**
 * Pick a discount to render: prefer fully-expanded rows (so % off / duration show).
 * Only use a synthetic placeholder when Stripe only sent unexpanded discount ids.
 * Sources are inspected in order — subscription discounts take precedence over
 * customer-level discounts when both exist.
 */
export function pickDiscountFromSources(sources: (DiscountRow | null | undefined)[]): DiscountRow | null {
  const allEntries: unknown[] = [];
  for (const src of sources) {
    if (src) allEntries.push(...collectDiscountEntries(src));
  }
  for (const entry of allEntries) {
    if (typeof entry === 'string') continue;
    if (!entry || typeof entry !== 'object') continue;
    const r = entry as DiscountRow;
    if (discountRowLooksExpanded(r)) return r;
  }
  for (const entry of allEntries) {
    if (typeof entry === 'string') {
      const id = entry.trim();
      if (id) {
        return { [UNEXPANDED_DISCOUNT]: true, id } as DiscountRow;
      }
    }
  }
  return null;
}

export function isUnexpandedDiscountPlaceholder(raw: DiscountRow | null): boolean {
  return Boolean(raw && raw[UNEXPANDED_DISCOUNT] === true);
}

/** Stripe internal ids — not shown as the “coupon name” in the title. */
export function looksLikeOpaqueStripeId(s: string): boolean {
  return /^(di_|cou_|cpo_|cgw_|sub_|price_|in_|ii_|dp_|du_)/i.test(s.trim());
}

/** A coupon's human-facing label: its Stripe **name**, else a readable id, else ''. */
export function couponLabel(coupon: Stripe.Coupon | null | undefined): string {
  if (!coupon) return '';
  const named = coupon.name != null && String(coupon.name).trim() ? String(coupon.name).trim() : '';
  if (named) return named;
  if (coupon.id && !looksLikeOpaqueStripeId(coupon.id)) return coupon.id;
  return '';
}

/** The lookup_key of the Lifetime price a gift card's coupon pays for (dabble-rest's lifetimeGiftKey), or null. */
export function lifetimeGiftKey(coupon: Pick<Stripe.Coupon, 'metadata'> | null | undefined): string | null {
  return coupon?.metadata?.lifetime_lookup_key || null;
}

/**
 * Human-facing label for a discount row: prefer Stripe coupon **name**, then
 * promotion code, then legacy fields.
 */
export function discountDisplayName(raw: DiscountRow | null): string {
  if (!raw || isUnexpandedDiscountPlaceholder(raw)) return '';

  const resolved = couponLabel(resolveCouponFromDiscountRow(raw));
  if (resolved) return resolved;

  const pc = raw.promotion_code;
  if (pc && typeof pc === 'object') {
    const p = pc as Stripe.PromotionCode;
    if (p.code && String(p.code).trim()) return String(p.code).trim();
  }

  const c = raw.coupon;
  if (typeof c === 'string') {
    const s = c.trim();
    if (s && !looksLikeOpaqueStripeId(s)) return s;
    return '';
  }
  return couponLabel(c && typeof c === 'object' ? (c as Stripe.Coupon) : null);
}

/** The terms a coupon carries, as far as the copy that describes it needs them. */
export interface CouponTerms {
  percent_off: number | null;
  amount_off: number | null;
  currency: string | null;
  duration: string;
  duration_in_months: number | null;
  currency_options?: Record<string, { amount_off: number }> | null;
}

/** The coupon as Stripe charges it in `currency`: an amount comes from its `currency_options` when it has one. */
export function couponInCurrency<T extends CouponTerms>(coupon: T, currency: string | null | undefined): T {
  const option = currency && coupon.amount_off != null ? coupon.currency_options?.[currency] : undefined;
  return option ? { ...coupon, amount_off: option.amount_off, currency } : coupon;
}

/** Months a billing period covers: 12 on annual. */
export function billingPeriodMonths(
  plan: { interval?: string | null; interval_count?: number | null } | null | undefined,
) {
  const count = plan?.interval_count || 1;
  return plan?.interval === 'year' ? 12 * count : plan?.interval === 'month' ? count : 1;
}

/** Months of a coupon: `once` is one, `repeating` its months, `forever` never runs out. */
export function couponMonths(coupon: Pick<CouponTerms, 'duration' | 'duration_in_months'>) {
  if (coupon.duration === 'forever') return Infinity;
  if (coupon.duration === 'repeating') return coupon.duration_in_months || 1;
  return 1;
}

export type CouponRefusal = 'COUPON_NOT_FOR_ANNUAL' | 'COUPON_ANNUAL_AT_PAYMENT_ONLY';

/**
 * Where dabble-rest puts a coupon on a plan billed every `period` months (couponFit's placeCoupon).
 * It attaches where its own terms fit that billing: any coupon on monthly, a forever percent, an
 * amount off once, or a whole number of years. A shorter one is a one-time line on an invoice being
 * paid (`atPayment`) and refused between payments; a forever amount, or months past a year that
 * aren't whole years, never goes on annual billing.
 */
export function placeCoupon(
  coupon: Pick<CouponTerms, 'duration' | 'duration_in_months' | 'percent_off' | 'amount_off'>,
  period: number,
  atPayment: boolean,
): 'attach' | 'line' | CouponRefusal {
  if (period <= 1) return 'attach';
  const months = couponMonths(coupon);
  if (months === Infinity) return coupon.percent_off != null ? 'attach' : 'COUPON_NOT_FOR_ANNUAL';
  if (coupon.amount_off != null && coupon.duration === 'once') return 'attach';
  if (coupon.percent_off != null && months % period === 0) return 'attach';
  if (months > period) return 'COUPON_NOT_FOR_ANNUAL';
  return atPayment ? 'line' : 'COUPON_ANNUAL_AT_PAYMENT_ONLY';
}

/** `sec` plus `months` calendar months in UTC, the way Stripe and dabble-rest count them: Jan 31 + 1 month is Feb's last day. */
export function addMonthsUtc(sec: number, months: number) {
  const d = new Date(sec * 1000);
  const target = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()),
  );
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d.getUTCDate(), lastDay));
  return target.getTime() / 1000;
}

/** Calendar months from `from` to `to`, the last one counted by the fraction of it that has passed. */
export function monthsBetween(from: number, to: number) {
  if (to <= from) return 0;
  const a = new Date(from * 1000);
  const b = new Date(to * 1000);
  let whole = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + b.getUTCMonth() - a.getUTCMonth();
  if (addMonthsUtc(from, whole) > to) whole--;
  const anchor = addMonthsUtc(from, whole);
  return whole + (to - anchor) / (addMonthsUtc(from, whole + 1) - anchor);
}

/** The billing cycle a discount runs on: when the current period was invoiced, its months, and whether it's a trial. */
export interface BillingCycle {
  start: number;
  months: number;
  trialing?: boolean;
}

const SAME_MOMENT = 60;

/**
 * The months of coupon a running discount still owes (dabble-rest's remainingMonths): its months
 * less what the invoices it has reached spent, the current one by the share of its period used.
 */
export function remainingMonths(
  discount: { start: number; end?: number | null },
  coupon: Pick<CouponTerms, 'duration' | 'duration_in_months'>,
  nowSec: number,
  cycle: BillingCycle,
) {
  const months = couponMonths(coupon);
  if (months === Infinity) return Infinity;
  if (discount.end && discount.end <= nowSec) return 0;
  if (cycle.trialing) return months;
  let used = 0;
  for (let k = 0; k < 600; k++) {
    const invoiced = addMonthsUtc(cycle.start, -k * cycle.months);
    if (invoiced + SAME_MOMENT < discount.start) break;
    used += k ? cycle.months : cycle.months * Math.min(1, monthsBetween(invoiced, nowSec) / cycle.months);
  }
  return Math.max(0, months - used);
}

/**
 * Whether a discount running on a subscription can move onto billing every `period` months of
 * `product` (dabble-rest's carryDiscounts, step for step). A forever percent or an amount off once
 * stays, and a forever amount can't. A coupon limited to other products is dropped. Anything else
 * is judged by the months it still owes, the way a code of that many months would be.
 */
export function carryRefusal(
  coupon: Parameters<typeof placeCoupon>[0] & Pick<Stripe.Coupon, 'applies_to'>,
  discount: { start: number; end?: number | null },
  period: number,
  { cycle, product, nowSec = Date.now() / 1000 }: { cycle: BillingCycle; product?: string; nowSec?: number },
): CouponRefusal | null {
  if (period <= 1) return null;
  if (coupon.duration === 'forever') return coupon.percent_off == null ? 'COUPON_NOT_FOR_ANNUAL' : null;
  if (coupon.duration === 'once' && coupon.amount_off != null) return null;
  const limitedTo = coupon.applies_to?.products;
  if (product && limitedTo?.length && !limitedTo.includes(product)) return null;
  if (!cycle.start && !cycle.trialing) return null;
  const months = remainingMonths(discount, coupon, nowSec, cycle);
  if (months <= 0) return null;
  return couponRefusal({ ...coupon, duration: 'repeating', duration_in_months: months }, period, true);
}

/**
 * The copy for a running discount yearly billing can't take: a forever one never will, and one with
 * an end has too long left, except on a trial, where its months don't start counting down.
 */
export function carryRefusalKey(coupon: Pick<Stripe.Coupon, 'duration'> | null | undefined, trialing = false) {
  return coupon && coupon.duration !== 'forever' && !trialing
    ? 'checkout_discount_over_a_year'
    : 'checkout_discount_monthly_only';
}

/** The API code dabble-rest refuses a coupon with on that billing, or null when it takes it. */
export function couponRefusal(
  coupon: Parameters<typeof placeCoupon>[0],
  period: number,
  atPayment: boolean,
): CouponRefusal | null {
  const placement = placeCoupon(coupon, period, atPayment);
  return placement === 'attach' || placement === 'line' ? null : placement;
}
